// File: base-http.service.ts
// Author: Excel Ortega
// Date: 4/27/2020
// Desc: Base service to handle http requests.
// Other Contributors: Patrick Wegner, Mason Beckham

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { cgpAuthentication } from '../../../modules/cgp-authentication';
import { ToastrService } from 'ngx-toastr';
import * as HttpStatus from 'http-status-codes';

const HTTP_STATUS_CODE_MESSAGES = {
   [HttpStatus.GONE]: 'Record does not exist',
   [HttpStatus.INTERNAL_SERVER_ERROR]: 'There was an error on the server',
   [HttpStatus.CONFLICT]: 'Record already exists',
   [HttpStatus.UNAUTHORIZED]: 'User does not have access',
   [0]: 'Connection was cancelled'
};


@Injectable({
   providedIn: 'root'
})
export class BaseHttpService {

   constructor(
      private readonly http: HttpClient,
      private readonly toastr: ToastrService
   ) { }

   get token(): string {
      return cgpAuthentication.getAuthToken();
   }

   get defaultHeaders(): HttpHeaders {
      const token = this.token;
      let headers = new HttpHeaders({
         'Content-Type': 'application/json',
         'Cache-Control': 'no-cache',
         'Pragma': 'no-cache',
         'Expires': '0',
      });

      if (!token || token.length === 0) {
         window.location.replace('#/logged-out');
      } else {
         headers = headers.set('Authorization', token);
      }

      return headers;
   }

   /**
    * Generate HttpParams to use on a HTTP request.
    *
    * @param {{ [key: string]: any }} params
    * @returns {HttpParams}
    * @memberof BaseHttpService
    */
   generateHttpParams(params: { [param: string]: any }): HttpParams {
      let p = new HttpParams();
      for (const el in params) {
         if (el && params[el] !== null && params[el] !== undefined) {
            p = p.set(el, params[el].toString());
         }
      }
      return p;
   }

   post(url, body?, options?: { [key: string]: any }) {
      if (!options) {
         options = {
            headers: this.defaultHeaders,
            // explicitly define the type
            // so TypeScript can statically infer
            // what type 'observe' is
            observe: 'response' as 'response'
         };
      }
      return this.http.post(url, body, options).pipe(
         map(httpResponse => this.handleData(httpResponse)),
         catchError(err => this.handleError(err))
      );
   }

   put(url, body) {
      const o = {
         headers: this.defaultHeaders,
         // explicitly define the type
         // so TypeScript can statically infer
         // what type 'observe' is
         observe: 'response' as 'response'
      };

      return this.http.put(url, body, o).pipe(
         map(httpResponse => {
            return this.handleData(httpResponse);
         }),
         catchError(err => this.handleError(err))
      );
   }

   get(url, options?: { [key: string]: any }) {
      const opts = (options) ? options : {};
      opts.headers = this.defaultHeaders;
      opts.observe = 'response';

      return this.http.get(url, opts).pipe(
         map(httpResponse => this.handleData(httpResponse)),
         catchError(err => this.handleError(err))
      );
   }

   delete(url, body?) {
      const o = {
         headers: this.defaultHeaders,
         // explicitly define the type
         // so TypeScript can statically infer
         // what type 'observe' is
         observe: 'response' as 'response',
         body
      };

      return this.http.request('delete', url, o).pipe(
         catchError(err => this.handleError(err))
      );
   }

   handleData(response) {
      let val;

      if (response.ok && response.status !== HttpStatus.NO_CONTENT) {
         val = response.body;
      } else {
         val = {
            data: [],
            totalCount: 0
         };
      }

      return val;
   }

   handleError(response: HttpErrorResponse, displayError = true) {
      console.error('Base HTTP Service Error:', response);
      if (response) {
         let message = '';

         if (response.error) {
            // Message sent by server for error
            message = message || response.error.data;

            // Default Validation error responses
            message = message || (response.error.errors || [])
               .map(error => error.defaultMessage)
               .join('<br>');
         }
         // Message for status codes without service message
         message = message || HTTP_STATUS_CODE_MESSAGES[response.status];

         // Default error
         message = message || 'Unknown reason for error.';

         if (displayError) {
            this.toastr.error(message, null, { enableHtml: true });
         }
      }
      return Promise.reject(response);
   }

}
